import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  IconButton,
  Stack,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClassicIDCard from "../emp-card/ClassicIDCard";
import ModernIDCard from "../emp-card/ModernIDCard";
import CreativeIDCard from "../emp-card/CreativeIDCard";
import authService from "../Auth/AuthService";
import DigitalIDCard from "../emp-card/DigitalIDCard";
import { printHtml } from "../utils/htmlToPrint";

const quickPalettes = [
  "#1e1e2f",
  "#0b6623",
  "#8a2be2",
  "#c68642",
  "#2b547e",
  "#b71c1c",
  "#004d40",
  "#0066cc",
];

export default function EmpIDCard() {
  const location = useLocation();
  const { employees } = location.state || [];

  const initialColors = useMemo(() => {
    const map = {};
    employees.forEach((s, i) => {
      map[s.id] = quickPalettes[i % quickPalettes.length];
    });
    return map;
  }, [employees]);

  const [colors, setColors] = useState(initialColors);
  const [globalColor, setGlobalColor] = useState("");
  const [fontcolor, setFontcolor] = useState("white");
  const [school, setSchool] = useState(null);
  // const [hide, setHide] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const templates = ['modern', 'classic', 'creative', 'digital'];
  const hasFetched = useRef(false);
  const [selectedCode, setSelectedCode] = useState('QRCode');

  const applyGlobalColor = () => {
    if (!globalColor) return;
    const next = { ...colors };
    employees.forEach((s) => (next[s.id] = globalColor));
    setColors(next);
  };

  const navigate = useNavigate();
  const DOC_URL = authService.DOC_URL

  const handleBack = () => navigate(-1); // go back one page

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadSchool();
  }, []);

  const loadSchool = async () => {
    try {
      const url = authService.pathUrl("GET", "/schools/email");
      const schoolsData = await authService.getAuth(url);
      const updatedForm = {
        ...schoolsData,
        logoUrl: DOC_URL + schoolsData.logoUrl
      };
      setSchool(updatedForm);
    } catch (error) {
      console.error("Error fetching schools:", error);
    }
  }

  const onPrint = () => {
    printHtml("emp-id-cards-root");
    window.location.reload();
    // setHide("none");
    // sessionStorage.setItem("hide", true);
    // setTimeout(() => {
    //   window.focus();
    //   window.print();
    //   setHide("block");
    //   sessionStorage.setItem("hide", false);
    // }, 500);
  };

  return (
    <Box sx={{ py: 1 }}>
      {/* Controls */}
      <Card sx={{ mb: 2, p: 2, }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <ColorLensIcon sx={{ fontSize: 50 }} />
            <Stack direction="row" spacing={1}>
              {quickPalettes.map((c) => (
                <IconButton
                  key={c}
                  onClick={() => {
                    setGlobalColor(c);
                    const next = { ...colors };
                    employees.forEach((s) => (next[s.id] = c));
                    setColors(next);
                  }}
                  sx={{ bgcolor: c, width: 30, height: 30, border: "1px solid rgba(0,0,0,0.15)" }}
                />
              ))}
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1">Global Color:</Typography>
            <input
              type="color"
              value={globalColor || "#000000"}
              onChange={(e) => setGlobalColor(e.target.value)}
              style={{ width: 42, height: 32, border: "none", background: "transparent" }}
            />
            <Button variant="contained" size="small" color="warning" onClick={applyGlobalColor}>
              Apply
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1">Font Color:</Typography>
            <input
              type="color"
              value={fontcolor || "#fff"}
              onChange={(e) => setFontcolor(e.target.value)}
              style={{ width: 42, height: 32, border: "none", background: "transparent" }}
            />
          </Stack>
          <Grid item xs={12}>
            <FormControl sx={{ minWidth: 200, mb: 0, minHeight: 10 }}>
              <InputLabel>Select Template</InputLabel>
              <Select
                size="small"
                value={selectedTemplate}
                label="Select Template"
                onChange={(e) => setSelectedTemplate(e.target.value)}
              >
                {templates.map((temp) => (
                  <MenuItem key={temp} value={temp}>
                    {temp.charAt(0).toUpperCase() + temp.slice(1)} Template
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl sx={{ minWidth: 200, mb: 0, minHeight: 10 }}>
              <InputLabel>Select Code</InputLabel>
              <Select
                size="small"
                value={selectedCode}
                label="Select Template"
                onChange={(e) => setSelectedCode(e.target.value)}
              >
                {["Barcode", "QRCode", "Empty"].map((temp) => (
                  <MenuItem key={temp} value={temp}>
                    {temp}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" sx={{ bgcolor: '#3f2d01', color: '#fff' }} startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </Button>

          <Button variant="outlined" sx={{ bgcolor: 'orange', color: '#fff' }} startIcon={<PrintIcon />} onClick={onPrint}>
            Print / Save as PDF
          </Button>
        </Stack>
      </Card>

      {/* Cards Grid */}

      <Box id="emp-id-cards-root">
        {selectedTemplate === 'modern' ? (
          <ModernIDCard data={employees} school={school} colors={colors} code={selectedCode} fontcolor={fontcolor} />
        ) : selectedTemplate === 'classic' ? (
          <ClassicIDCard data={employees} school={school} colors={colors} code={selectedCode} fontcolor={fontcolor} />
        ) : selectedTemplate === 'creative' ? (
          <CreativeIDCard data={employees} school={school} colors={colors} code={selectedCode} fontcolor={fontcolor} />
        ) : selectedTemplate === 'digital' ? (
          <DigitalIDCard data={employees} school={school} colors={colors} code={selectedCode} fontcolor={fontcolor} />
        ) : null}
      </Box >
    </Box >
  );
}